import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/Shared/Button';
import { Input } from '../../components/Shared/Input';
import { Modal } from '../../components/Shared/Modal';
import { Table } from '../../components/Shared/Table';
import { useToast } from '../../components/UI/ToastProvider';
import { calendarService, type CalendarDto } from '../../services/calendarService';
import {
  slaService,
  type CreateSlaDto,
  type SlaCountType,
  type SlaDto,
  type UpdateSlaDto,
} from '../../services/slaService';

interface SlaFormState {
  id: string | null;
  name: string;
  description: string;
  countType: SlaCountType;
  calendarId: string;
  days: number;
  hours: number;
  minutes: number;
  alertPercentage: number;
  priorityRule: string;
}

const DEFAULT_FORM: SlaFormState = {
  id: null,
  name: '',
  description: '',
  countType: 'Business',
  calendarId: '',
  days: 0,
  hours: 8,
  minutes: 0,
  alertPercentage: 80,
  priorityRule: '',
};

function getCustomerIdFromStorage(): string {
  return sessionStorage.getItem('@KnowInDoc:customer_id')?.trim() ?? '';
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) return null;
    const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function getTenantNameFromSession(): string {
  const userRaw = sessionStorage.getItem('@KnowInDoc:user');
  if (userRaw) {
    try {
      const user = JSON.parse(userRaw) as Record<string, unknown>;
      if (typeof user.customerName === 'string' && user.customerName.trim()) return user.customerName.trim();
      if (typeof user.customer_name === 'string' && user.customer_name.trim()) return user.customer_name.trim();
      if (typeof user.tenantName === 'string' && user.tenantName.trim()) return user.tenantName.trim();
    } catch {
      // ignore parse error and continue fallback
    }
  }

  const accessToken = sessionStorage.getItem('@KnowInDoc:accessToken');
  if (accessToken) {
    const payload = decodeJwtPayload(accessToken);
    const fromToken =
      (typeof payload?.CustomerName === 'string' && payload.CustomerName) ||
      (typeof payload?.customerName === 'string' && payload.customerName) ||
      (typeof payload?.tenantName === 'string' && payload.tenantName) ||
      '';
    if (fromToken.trim()) return fromToken.trim();
  }

  return 'tenant';
}

function minutesToParts(totalMinutes: number): { days: number; hours: number; minutes: number } {
  const safeTotal = Math.max(0, Number.isFinite(totalMinutes) ? Math.floor(totalMinutes) : 0);
  const days = Math.floor(safeTotal / (8 * 60));
  const afterDays = safeTotal - days * 8 * 60;
  const hours = Math.floor(afterDays / 60);
  const minutes = afterDays - hours * 60;
  return { days, hours, minutes };
}

function partsToMinutes(days: number, hours: number, minutes: number): number {
  const d = Math.max(0, Math.floor(days || 0));
  const h = Math.max(0, Math.floor(hours || 0));
  const m = Math.max(0, Math.floor(minutes || 0));
  return d * 8 * 60 + h * 60 + m;
}

function formatSlaDuration(totalMinutes: number): string {
  const { days, hours, minutes } = minutesToParts(totalMinutes);
  return `${days}d ${hours}h ${minutes}min`;
}

export const SlaPage: React.FC = () => {
  const customerId = useMemo(() => getCustomerIdFromStorage(), []);
  const tenantName = useMemo(() => getTenantNameFromSession(), []);
  const { addToast } = useToast();

  const [slas, setSlas] = useState<SlaDto[]>([]);
  const [calendars, setCalendars] = useState<CalendarDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<SlaFormState>(DEFAULT_FORM);

  const calendarsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const calendar of calendars) {
      map.set(calendar.id, calendar.name);
    }
    return map;
  }, [calendars]);

  const loadData = useCallback(async () => {
    if (!customerId) {
      setError('Cliente não identificado na sessão. Faça login novamente.');
      setSlas([]);
      setCalendars([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const [slaItems, customerCalendars] = await Promise.all([
        slaService.getSlas(customerId),
        calendarService.getCalendars(customerId),
      ]);

      const calendarsById = new Map(customerCalendars.map((calendar) => [calendar.id, calendar]));
      const missingCalendarIds = Array.from(
        new Set(
          slaItems
            .map((sla) => sla.calendarId)
            .filter((calendarId): calendarId is string => Boolean(calendarId && !calendarsById.has(calendarId)))
        )
      );

      if (missingCalendarIds.length > 0) {
        const resolvedCalendars = await Promise.all(
          missingCalendarIds.map((calendarId) => calendarService.getCalendarById(calendarId))
        );

        for (const calendar of resolvedCalendars) {
          if (calendar) calendarsById.set(calendar.id, calendar);
        }
      }

      setSlas(slaItems);
      setCalendars(Array.from(calendarsById.values()));
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Erro ao carregar dados de SLA.';
      setError(message);
      addToast(message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [customerId, addToast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const openCreateModal = () => {
    setForm(DEFAULT_FORM);
    setIsModalOpen(true);
  };

  const openEditModal = (item: SlaDto) => {
    const parts = minutesToParts(item.timeInMinutes);
    setForm({
      id: item.id,
      name: item.name ?? '',
      description: item.description ?? '',
      countType: item.countType ?? 'Business',
      calendarId: item.calendarId ?? '',
      days: parts.days,
      hours: parts.hours,
      minutes: parts.minutes,
      alertPercentage: item.alertPercentage ?? 80,
      priorityRule: item.priorityRule ?? '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setIsModalOpen(false);
    setForm(DEFAULT_FORM);
  };

  const deleteSla = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este SLA?')) return;
    setIsDeleting(id);
    setError(null);
    try {
      await slaService.deleteSla(id);
      addToast('SLA excluído com sucesso.', 'success');
      await loadData();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Erro ao excluir SLA.';
      setError(message);
      addToast(message, 'error');
    } finally {
      setIsDeleting(null);
    }
  };

  const saveSla = async () => {
    if (!customerId) {
      setError('Cliente não identificado na sessão.');
      return;
    }
    if (!form.name.trim()) {
      setError('Informe o nome do SLA.');
      return;
    }
    if (form.alertPercentage < 0 || form.alertPercentage > 100) {
      setError('O percentual de alerta deve estar entre 0 e 100.');
      return;
    }

    const timeInMinutes = partsToMinutes(form.days, form.hours, form.minutes);
    if (timeInMinutes <= 0) {
      setError('Informe um tempo total maior que zero.');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const basePayload = {
        customerId,
        calendarId: form.countType === 'Running' ? null : form.calendarId || null,
        name: form.name.trim(),
        description: form.description.trim() || null,
        timeInMinutes,
        alertPercentage: form.alertPercentage,
        countType: form.countType,
        priorityRule: form.priorityRule.trim() || null,
      };

      if (form.id) {
        const payload: UpdateSlaDto = basePayload;
        await slaService.updateSla(form.id, payload);
        addToast('SLA atualizado com sucesso.', 'success');
      } else {
        const payload: CreateSlaDto = basePayload;
        await slaService.createSla(payload);
        addToast('SLA criado com sucesso.', 'success');
      }

      setIsModalOpen(false);
      setForm(DEFAULT_FORM);
      await loadData();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Erro ao salvar SLA.';
      setError(message);
      addToast(message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const columns = [
    {
      header: 'Nome',
      accessor: 'name',
      cell: (row: SlaDto) => (
        <div>
          <p className="font-medium text-gray-900">{row.name}</p>
          {row.description ? <p className="text-sm text-gray-500">{row.description}</p> : null}
        </div>
      ),
    },
    {
      header: 'Calendário associado',
      accessor: 'calendar',
      cell: (row: SlaDto) => (
        <span className="text-gray-900">
          {row.calendarId
            ? `${tenantName} - ${calendarsById.get(row.calendarId) ?? 'Calendário não encontrado'}`
            : `${tenantName} - Calendário Global`}
        </span>
      ),
    },
    {
      header: 'Tipo',
      accessor: 'countType',
      cell: (row: SlaDto) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {row.countType}
        </span>
      ),
    },
    {
      header: 'Tempo total',
      accessor: 'timeInMinutes',
      cell: (row: SlaDto) => <span className="text-gray-900">{formatSlaDuration(row.timeInMinutes)}</span>,
    },
    {
      header: 'Alerta',
      accessor: 'alertPercentage',
      cell: (row: SlaDto) => <span className="text-gray-900">{row.alertPercentage}%</span>,
    },
    {
      header: 'Ações',
      accessor: 'actions',
      cell: (row: SlaDto) => (
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => openEditModal(row)}>
            Editar
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => void deleteSla(row.id)}
            disabled={isDeleting === row.id}
          >
            {isDeleting === row.id ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold">SLAs</h1>
          <p className="text-sm text-gray-600 mt-1">Gerencie tempos, alertas e tipo de contagem por SLA.</p>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          Novo SLA
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 text-red-800 px-4 py-3 text-sm mb-4 flex justify-between gap-4">
          <span>{error}</span>
          <button type="button" className="underline font-medium" onClick={() => setError(null)}>
            Fechar
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700" />
        </div>
      ) : (
        <Table data={slas} columns={columns} />
      )}

      <Modal isOpen={isModalOpen} onClose={closeModal} title={form.id ? 'Editar SLA' : 'Novo SLA'}>
        <div className="space-y-4">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700">Descrição</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Tipo de contagem</label>
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
              value={form.countType}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  countType: e.target.value as SlaCountType,
                  calendarId: e.target.value === 'Running' ? '' : prev.calendarId,
                }))
              }
            >
              <option value="Business">Tempo útil (considera calendário)</option>
              <option value="Running">Tempo corrido (wall-clock)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Calendário</label>
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm disabled:bg-gray-100 disabled:text-gray-500"
              value={form.calendarId}
              onChange={(e) => setForm((prev) => ({ ...prev, calendarId: e.target.value }))}
              disabled={form.countType === 'Running'}
            >
              <option value="">Calendário Global</option>
              {calendars.map((calendar) => (
                <option key={calendar.id} value={calendar.id}>
                  {calendar.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tempo (dias + horas + minutos)</label>
            <div className="grid grid-cols-3 gap-2">
              <Input
                label="Dias"
                type="number"
                min={0}
                value={String(form.days)}
                onChange={(e) => setForm((prev) => ({ ...prev, days: Math.max(0, Number(e.target.value) || 0) }))}
              />
              <Input
                label="Horas"
                type="number"
                min={0}
                value={String(form.hours)}
                onChange={(e) => setForm((prev) => ({ ...prev, hours: Math.max(0, Number(e.target.value) || 0) }))}
              />
              <Input
                label="Minutos"
                type="number"
                min={0}
                value={String(form.minutes)}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, minutes: Math.max(0, Number(e.target.value) || 0) }))
                }
              />
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Conversão interna: 1 dia útil = 8 horas. Total em minutos = dias * 8 * 60 + horas * 60 + minutos.
            </p>
          </div>

          <Input
            label="Percentual de alerta (0 a 100)"
            type="number"
            min={0}
            max={100}
            value={String(form.alertPercentage)}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                alertPercentage: Math.min(100, Math.max(0, Number(e.target.value) || 0)),
              }))
            }
            required
          />
          <p className="text-xs text-gray-600 -mt-3">
            Ao cruzar este percentual, SLA será marcado como &quot;Em risco&quot; (amarelo).
          </p>

          <Input
            label="Regra de prioridade"
            value={form.priorityRule}
            onChange={(e) => setForm((prev) => ({ ...prev, priorityRule: e.target.value }))}
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={closeModal} disabled={isSaving}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => void saveSla()} disabled={isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
